import clsx from "clsx";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalFooter,
  FullScreenModalHeader,
  Loader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { Color } from "@transfr-inc/dashboard-components/utils";

import {
  trackClassroomCreated,
  trackInstructorsAssignedToClassroom,
  trackLearnersAssignedToClassroom,
  trackProgramAssignedToClassroom,
} from "@lib/event-trackers";
import container from "@src/container";

import { ClassroomInfo } from "./classroom-info";
import { ConfirmDemoModal } from "./confirm-demo-modal";
import {
  BUILD_YOUR_OWN_PROGRAM,
  getProductIdsForClassroomCreation,
  useClassroomCreationData,
} from "./new-classroom.utils.js";

import { ProfessionalLearningProduct } from "@components/custom/dropdowns/product-dropdown-list";

import "./new-classroom-modal.scss";

export const NewClassroomModal = ({
  onClose,
  show,
  orgCode,
  general_orgSelectionEnabled = true,
  instructorCreationEnabled = true,
  className,
}) => {
  // If org is selectable, do not use passed in code - passed in code is the multiorg code, not an org code
  const [loading, setLoading] = useState();
  const [confirmDemoOpen, setConfirmDemoOpen] = useState();
  const [selectedOrg, setSelectedOrg] = useState();

  const currentOrg = useMemo(() => {
    if (general_orgSelectionEnabled) {
      return selectedOrg ? { code: selectedOrg } : undefined;
    }
    return { code: orgCode };
  }, [selectedOrg]);

  const {
    availableProducts,
    programs,
    isLoading: loadingPreCalculatedData,
  } = useClassroomCreationData(currentOrg?.code, general_orgSelectionEnabled);

  // NOTE: specifying a default value for trainees and instructors results in state not being saved, perhaps
  // because they are arrays? Default values have been specified at the <Controller> node level
  const methods = useForm({
    criteriaMode: "all",
    mode: "onChange",
    defaultValues: {
      name: "",
      organization: currentOrg,
      product: "",
      isDemo: false,
    },
  });

  methods.formState.isValid;
  methods.formState.errors;

  const { handleSubmit, reset, formState, watch } = methods;

  const { isValid } = formState;

  const { classroomService } = container;

  const handleClassroomCreationTracking = async ({
    classroomId,
    classroomName,
    traineeIds,
    instructorIds,
    programId,
    programName,
    isDemo,
    productList,
  }) => {
    const trackingPromises = [];

    trackingPromises.push(
      trackClassroomCreated(
        classroomId,
        classroomName,
        isDemo ? "demo" : "regular",
        String(productList)
      )
    );

    if (traineeIds && traineeIds.length > 0) {
      trackingPromises.push(
        trackLearnersAssignedToClassroom(traineeIds, classroomId)
      );
    }

    if (programId && programName) {
      trackingPromises.push(
        trackProgramAssignedToClassroom(programId, programName, classroomId)
      );
    }

    if (instructorIds && instructorIds.length > 0) {
      trackingPromises.push(
        trackInstructorsAssignedToClassroom(instructorIds, classroomId)
      );
    }

    try {
      await Promise.allSettled(trackingPromises);
    } catch (error) {
      console.error("Error tracking classroom events:", error);
    }
  };

  const submitForm = async (values) => {
    setLoading(true);
    try {
      const instructorIds = values.instructors.map(
        (optionItem) => optionItem.id
      );

      const traineeIds = values.trainees?.map((optionItem) => optionItem.id);

      const isProfessionalLearning =
        values.product.id === ProfessionalLearningProduct.id;

      const productList = getProductIdsForClassroomCreation(values.product);

      const isBuildYourOwn = values.program?.id === BUILD_YOUR_OWN_PROGRAM.id;
      const programId = isBuildYourOwn ? undefined : values.program?.id;
      const programName = isBuildYourOwn ? undefined : values.program?.title;

      const classroom = {
        organizationCode: values.organization.code,
        productIds: productList,
        name: values.name,
        instructorIds: instructorIds,
        traineeIds: traineeIds,
        sessionTimeLimit: 0,
        isDemo: values.isDemo,
        programId: programId,
        professionalLearning: isProfessionalLearning,
        buildYourOwnCourse: isBuildYourOwn,
      };

      const response = await classroomService.saveClassroom(classroom);

      if (response) {
        const classroomId = response.classroom_id;

        await handleClassroomCreationTracking({
          classroomId,
          classroomName: values.name,
          traineeIds,
          instructorIds,
          programId,
          programName,
          isDemo: values.isDemo,
          productList,
        });
      }
      const notification = {
        type: NotificationType.success,
        message: `${values.name} has been created.`,
      };
      setLoading();
      return closeDialog(/* confirm: */ true, notification);
    } catch (error) {
      console.error(error);
      const notification = {
        type: NotificationType.error,
        message: `Error creating classroom: ${error.data}`,
      };
      setLoading();
      return closeDialog(/* confirm: */ true, notification);
    } finally {
      setLoading();
    }
  };

  const closeDialog = (confirm, notification) => {
    reset();
    onClose(notification);
    return confirm;
  };

  const cancelDialog = () => {
    return closeDialog(/* confirm: */ false);
  };

  const onCreateClick = (e) => {
    e.preventDefault();
    setConfirmDemoOpen();
    handleSubmit(submitForm)();
  };

  const onConfirmCreateClick = (e) => {
    const values = watch();
    if (values.isDemo) {
      setConfirmDemoOpen(true);
    } else {
      onCreateClick(e);
    }
  };

  return (
    <>
      <FullScreenModal
        className="create-classroom-modal"
        onClose={cancelDialog}
        open={show}
        drawerBgColor={Color.white}
      >
        <FullScreenModalHeader
          title="Create a Classroom"
          titleIcon="fa-regular fa-screen-users"
        />
        <FullScreenModalBody>
          <div className="create-classroom">
            {(loading || loadingPreCalculatedData) && <Loader overlay></Loader>}
            <div className="classroom-info">
              <FormProvider {...methods}>
                <form
                  className={clsx("account-form", className)}
                  noValidate={true}
                >
                  <ClassroomInfo
                    products={availableProducts}
                    programs={programs}
                    orgSelectionEnabled={general_orgSelectionEnabled}
                    instructorCreationEnabled={instructorCreationEnabled}
                    setSelectedOrg={setSelectedOrg}
                  />
                </form>
              </FormProvider>
            </div>
          </div>
        </FullScreenModalBody>
        <FullScreenModalFooter>
          <Button disabled={loading} onClick={cancelDialog}>
            Cancel
          </Button>
          <Button
            primary
            disabled={!isValid || loading}
            onClick={onConfirmCreateClick}
          >
            Create Classroom
          </Button>
        </FullScreenModalFooter>
      </FullScreenModal>
      <ConfirmDemoModal
        open={confirmDemoOpen}
        onBackToModal={setConfirmDemoOpen}
        onConfirmClose={onCreateClick}
      ></ConfirmDemoModal>
    </>
  );
};
